import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/components/Button';
import DownloadModal from '@/components/DownloadModal';
import { useMediaQuery } from '@/hooks';
import {
	FEATURE_NAMES,
	useIsCollaborator,
	useIsFeatureBlocked
} from '@/hooks/user';
import Close from '@/icons/close.svg';
import Delete from '@/icons/delete.svg';
import Download from '@/icons/download.svg';
import Folder from '@/icons/folder.svg';
import Mail from '@/icons/mail.svg';
import Print from '@/icons/print.svg';
import {
	setIsModalUpgradePlanOpen,
	setModalUpgradePlanScope
} from '@/store/profile';
import { cn } from '@/utils';

import SaveToFolderModal from './SaveToFolderModal';
import SendDocumentModal from './SendDocumentModal';
import { PrintDocument, RemoveDocument } from './modals';

function SelectionOptions({ rowSelection, number, reset }) {
	const { t } = useTranslation();
	const transactions = useSelector(state => state.myDocuments.transactions);
	const IS_FEATURE_BLOCKED = useIsFeatureBlocked();
	const isCollaborator = useIsCollaborator();
	const dispatch = useDispatch();
	const selectedTransactions = transactions.filter((_, i) =>
		Object.keys(rowSelection).some(j => i === Number(j))
	);
	const mdDown = useMediaQuery('mdDown');
	const [openOption, setOpenOption] = useState(null);
	if (!number) return;

	const defaultOptions = [
		{
			name: FEATURE_NAMES.DELETE,
			Component: RemoveDocument,
			Icon: Delete,
			isOutline: true,
			shouldHideWhenMultipleDocs: false,
			text: t('common.delete'),
			isFeatureBlocked: IS_FEATURE_BLOCKED[FEATURE_NAMES.DELETE]
		},
		{
			name: FEATURE_NAMES.SEND,
			Component: SendDocumentModal,
			Icon: Mail,
			isOutline: true,
			shouldHideWhenMultipleDocs: true,
			text: t('common.send'),
			isFeatureBlocked: IS_FEATURE_BLOCKED[FEATURE_NAMES.SEND]
		},
		{
			name: FEATURE_NAMES.PRINT,
			Component: PrintDocument,
			Icon: Print,
			isOutline: true,
			shouldHideWhenMultipleDocs: true,
			text: t('common.print'),
			isFeatureBlocked: IS_FEATURE_BLOCKED[FEATURE_NAMES.PRINT]
		},
		{
			name: FEATURE_NAMES.SAVE,
			Component: SaveToFolderModal,
			Icon: Folder,
			isOutline: true,
			shouldHideWhenMultipleDocs: false,
			text: t('myDocuments.table.dropdown.saveToFolder'),
			isFeatureBlocked: IS_FEATURE_BLOCKED[FEATURE_NAMES.SAVE]
		},
		{
			name: FEATURE_NAMES.DOWNLOAD,
			Component: DownloadModal,
			Icon: Download,
			isOutline: false,
			shouldHideWhenMultipleDocs: false,
			text: t('common.download'),
			isFeatureBlocked: IS_FEATURE_BLOCKED[FEATURE_NAMES.DOWNLOAD]
		}
	];

	const optionsCollaborator = defaultOptions.filter(
		({ name }) => name !== FEATURE_NAMES.DELETE
	);
	const options = isCollaborator ? optionsCollaborator : defaultOptions;

	return (
		<div className="fixed bottom-0 left-0 right-0 mx-auto flex w-full max-w-[94.5rem] justify-between rounded border border-grey-200 bg-white p-4 md:bottom-5 md:left-[268px] md:w-[calc(100%-284px)]">
			<Button
				className="flex gap-2 py-[2px] text-xs font-semibold leading-5 md:h-6 md:pl-2 md:pr-3"
				variant="ghost"
				size={mdDown ? 'iconMd' : 'default'}
				onClick={reset}
			>
				{mdDown && <Close className="h-4 w-4 text-black" />}
				{!mdDown && t('common.cancel')}
			</Button>

			<div className="flex gap-2">
				{options.map(
					({
						Icon,
						isOutline,
						name,
						shouldHideWhenMultipleDocs,
						text,
						isFeatureBlocked
					}) => (
						<Button
							aria-label={text}
							className={cn(
								'flex gap-2 py-[2px] text-xs font-semibold leading-5 md:h-6 md:pl-2 md:pr-3',
								shouldHideWhenMultipleDocs && number > 1 && 'hidden'
							)}
							key={name}
							onClick={event => {
								event.preventDefault();

								if (isFeatureBlocked) {
									dispatch(setIsModalUpgradePlanOpen(true));
									dispatch(setModalUpgradePlanScope(name));

									return;
								}

								setOpenOption(name);
							}}
							size={mdDown ? 'iconMd' : 'default'}
							variant={isOutline ? 'outline' : undefined}
						>
							<Icon
								aria-hidden
								className={`h-4 w-4 ${isOutline ? 'text-black' : 'text-white'}`}
							/>
							{!mdDown && text}
						</Button>
					)
				)}
			</div>

			<div className="flex items-center gap-1">
				<span className="text-xs font-bold text-grey-500">{number} </span>
				<span className="text-xs text-grey-500">
					{!mdDown && t('common.selected')}
				</span>
			</div>

			{options.map(({ Component, name }) => {
				return (
					<Component
						key={name}
						open={openOption === name}
						onOpenChange={setOpenOption}
						selectedTransactions={selectedTransactions}
					/>
				);
			})}
		</div>
	);
}

export default SelectionOptions;
